import { Component, computed, effect, inject, OnDestroy, untracked, ChangeDetectionStrategy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { SettingsService } from '../services/settings.service';
import { DataService } from '../services/data.service';
import { ApiService } from '../services/api.service';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { LoaderDiagComponent } from '../shared/loader-diag/loader-diag.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';

@Component({
  selector: 'app-shell',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    AsyncPipe,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatSidenavModule,
    MatListModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCardModule,
    MatDialogModule,
    MatInputModule,
    MatDividerModule,
    MatChipsModule
  ],
  templateUrl: './shell.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './shell.component.scss'
})
export class ShellComponent implements OnDestroy {
  title = 'Reporting Portal';

  private breakpointObserver = inject(BreakpointObserver);
  readonly auth = inject(AuthService);
  readonly settings = inject(SettingsService);
  readonly data = inject(DataService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly api = inject(ApiService);

  /** Locale options for date/time display. */
  readonly locales = this.settings.locales;

  /** Time-zone options, each labelled with its current UTC offset. */
  readonly timezones = this.settings.timezones;

  /** The re-authenticating dialog, while one is open. */
  private reauthDialog: MatDialogRef<LoaderDiagComponent> | null = null;

  readonly sitesLabel = computed(() => {
    const sites = this.auth.sites();
    if (!sites) {
      return '—';
    }
    if (sites === 'all') {
      return 'All';
    }
    const count = Number(sites);
    return Number.isFinite(count)
      ? new Intl.NumberFormat(this.settings.locale()).format(count)
      : sites;
  });

  readonly today = this.formatDate(new Date().toISOString());

  constructor() {

    // Filter options are held in memory only, so every authenticated load —
    // a fresh sign-in or a page refresh — fetches them again.
    this.api.loadFilterOptions().subscribe();

    toObservable(this.auth.refreshing).subscribe(refreshing => {

      if (refreshing == true) {
        this.openReauthDialog();
      } else {
        this.closeReauthDialog();
      }

    })
    
  }

  ngOnDestroy(): void {
    this.closeReauthDialog();
  }

  private openReauthDialog(): void {
    // One at a time, and never stacked on a loader that is already blocking
    // the UI — an export's own request is what usually triggers this refresh,
    // and a second backdrop over the first tells the user nothing new.
    if (this.reauthDialog || this.dialog.openDialogs.length > 0) {
      return;
    }
    this.reauthDialog = this.dialog.open(LoaderDiagComponent, {
      data: { loaderMessage: 'Re-authenticating...' },
      disableClose: true
    });
  }

  private closeReauthDialog(): void {
    this.reauthDialog?.close();
    this.reauthDialog = null;
  }

  /** True on phone-sized viewports — drives sidenav mode and default open state. */
  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map(result => result.matches),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Locale is display-only: the datepicker reads it straight off SettingsService
   * (see PortalDateAdapter), so nothing needs to be pushed into the adapter.
   */
  onLocaleChange(locale: string): void {
    this.settings.setLocale(locale);
  }

  onTimezoneChange(timezone: string): void {
    this.settings.setTimezone(timezone);
  }

  copyEmail() {
    navigator.clipboard.writeText('support@privacy4cars.com').finally(() => {
      this.snackBar.open('Email ID Copied to Clipboard', 'Dismiss', { duration: 5000 });
    })
  }

  /** Collapse the drawer after navigation, but only on handset where it overlays content. */
  closeIfHandset(drawer: MatSidenav): void {
    if (this.breakpointObserver.isMatched(Breakpoints.Handset)) {
      drawer.close();
    }
  }

  logout(): void {
    this.auth.logout();
  }

  formatDate(iso: string): string {
    return new Intl.DateTimeFormat(this.settings.locale(), {
      dateStyle: 'full',
      /* timeStyle: 'short', */
      /* timeZone: this.settings.timezone() */
    }).format(new Date(iso));
  }

}
